<template>
  <AppTable ref="table" :fields="fields" :items="items" primary-key="id" v-bind="$attrs" v-on="$listeners">
    <template #cell(interact)="{ item }">
      <div v-if="deleteConfirmations[item.id]">
        <span>Do you want to delete the scrim?</span>
        <div class="mt-1">
          <button role="button" class="btn btn-primary mr-2" @click="hideDeleteConfirmation(item)">No</button>
          <button role="button" class="btn btn-warning ml-2" @click="deleteScrim(item)">Yes</button>
        </div>
      </div>
      <div v-else>
        <button
          role="button"
          class="btn btn-outline-primary action action--stats"
          @click="showDeleteConfirmation(item)"
        >
          <b-icon-trash></b-icon-trash> Delete
        </button>
      </div>
    </template>

    <template #cell(date)="{ item }">
      <div class="column">
        <time :datetime="item.startedAt | isoString" class="column">
          <span>{{ item.startedAt | localDate }}</span>
          <span>{{ item.startedAt | localTime }}</span>
        </time>
      </div>
    </template>

    <template #cell(mapId)="{ item }">
      <MapCell :id="item.mapId" v-if="item.mapId" variant="col" size="lg" />
      <span v-else class="no-data">N/A</span>
    </template>

    <template #cell(team1)="{ item }">
      <div class="column">
        <AgentComposition :agent_ids="item.allyPlayers.map(a => a.agentId)" size="sm" />
      </div>
    </template>

    <template #cell(score)="{ item }">
      <div class="score">
        <span class="score__result" :class="`score__result--${item.allyScore > item.enemyScore ? 'win' : 'loss'}`">{{
          item.allyScore
        }}</span>
        <span class="score__separator">:</span>
        <span class="score__result" :class="`score__result--${item.allyScore <= item.enemyScore ? 'win' : 'loss'}`">{{
          item.enemyScore
        }}</span>
      </div>
    </template>

    <template #cell(team2)="{ item }">
      <div class="column">
        <AgentComposition :agent_ids="item.enemyPlayers.map(a => a.agentId)" size="sm" />
      </div>
    </template>

    <template #cell(actions)="{ item }">
      <div class="column">
        <button class="btn btn-outline-primary action action--stats" @click="getDataAndExport(item.id)">Export</button>
      </div>
    </template>
  </AppTable>
</template>

<script>
import { BIconTrash } from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import XLSX from 'xlsx'

import AgentComposition from '@/components/UI/AgentComposition.vue'
import { processMatchData } from '@/utils/processScrimExport'

import { getOWScrimData } from '../../api/ow-scrims.js'
import isoString from '../../filters/isoString.js'
import localDate from '../../filters/localDate.js'
import localTime from '../../filters/localTime.js'
import AppTable from '../generic/Table.vue'
import MapCell from '../Table/cells/MapCell.vue'

export default {
  name: 'OWScrimTable',
  components: {
    AppTable,
    MapCell,
    AgentComposition,
    BIconTrash,
  },
  data() {
    return {
      deleteConfirmations: {},
    }
  },
  props: ['items'],
  computed: {
    ...mapGetters({
      getMapById: 'static/getMapById',
    }),
    fields() {
      return [
        { key: 'interact', label: '' },
        { key: 'date', label: 'Date', formatter: localDate },
        { key: 'mapId', label: 'Map' },
        { key: 'team1', label: 'Team' },
        { key: 'score', label: 'vs' },
        { key: 'team2', label: 'Opponent' },
        { key: 'actions', label: '' },
      ]
    },
  },
  filters: {
    localDate,
    localTime,
    isoString,
  },
  methods: {
    async getDataAndExport(id) {
      try {
        let scrimData = await getOWScrimData(id)
        if (Object.keys(scrimData).length === 0) {
          console.log('no data')
          return
        }
        let parsedData = processMatchData(scrimData)

        const workbook = XLSX.utils.book_new()
        const matchWorksheet = XLSX.utils.json_to_sheet([parsedData.match])
        const roundsWorksheet = XLSX.utils.json_to_sheet(parsedData.rounds)
        const playersWorksheet = XLSX.utils.json_to_sheet(parsedData.playerStats)
        const killsWorksheet = XLSX.utils.json_to_sheet(parsedData.kills)
        XLSX.utils.book_append_sheet(workbook, matchWorksheet, 'Match')
        XLSX.utils.book_append_sheet(workbook, roundsWorksheet, 'Rounds')
        XLSX.utils.book_append_sheet(workbook, playersWorksheet, 'Round Player')
        XLSX.utils.book_append_sheet(workbook, killsWorksheet, 'Kills')
        XLSX.writeFile(
          workbook,
          `${parsedData.match.map} - ${parsedData.match.date} - ${parsedData.match.matchId}.xlsx`
        )
      } catch (error) {
        console.log(error)
      }
    },
    showDeleteConfirmation(item) {
      this.$set(this.deleteConfirmations, item.id, true)
    },
    hideDeleteConfirmation(item) {
      this.$set(this.deleteConfirmations, item.id, false)
    },
    deleteScrim(item) {
      this.hideDeleteConfirmation(item)
      this.$emit('deleteScrim', item)
    },
  },
}
</script>

<style lang="scss">
.column {
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
}

.score {
  height: 100%;
  display: block;
  text-align: center;
  white-space: nowrap;

  &__result {
    font-weight: 700;
    font-size: 30px;
    line-height: 1;
    display: inline-block;
    width: 1.5em;

    &:first-of-type {
      text-align: right;
    }

    &:last-of-type {
      text-align: left;
    }

    &--win {
      color: $highlight;
    }
  }

  &__separator {
    font-weight: 400;
    font-size: 30px;
    line-height: 0.8;
    min-width: 0.75em;
    display: inline-block;
  }
}

.cell {
  &--score {
    text-align: center;
  }
}
</style>
